import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { ticketService, isTicketApiSuccess } from '../../api/ticketService';
import { getUsersWithRole } from '../../api/roleManagementService';
import { extractErrorMessage } from '../../utils/errorHandler';
import QueueConfidentialityLevelControl from './QueueConfidentialityLevelControl';

/**
 * HDO / TeamTickets inquiry assignment modal (Level 1 role + inquiry priority + remarks).
 * Authorization to open is enforced by the caller (e.g. pending-team-lead-assignments probe).
 */
export default function InquiryAssignModal({
  ticket,
  isOpen,
  onClose,
  onAssigned,
}) {
  const ticketId = ticket?.ticketId ?? ticket?.id;
  const [assigning, setAssigning] = useState(false);
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [selectedAssignPriorityId, setSelectedAssignPriorityId] = useState('');
  const [assignResponsibleRoleId, setAssignResponsibleRoleId] = useState(null);
  const [assignResponsibleRoleName, setAssignResponsibleRoleName] = useState('');
  const [assignEscalationLevelName, setAssignEscalationLevelName] = useState('');
  const [assignUsers, setAssignUsers] = useState([]);
  const [selectedAssignUserId, setSelectedAssignUserId] = useState('');
  const [assignmentRemarks, setAssignmentRemarks] = useState('');
  const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);

  const assignmentInquiryPriorities = useMemo(
    () =>
      (inquiryPriorityLevels || []).filter((p) => {
        const code = String(p.priorityLevelCode || p.code || '').toLowerCase().replace(/[\s-]+/g, '_');
        const name = String(p.priorityLevelName || p.name || '').toLowerCase();
        return code !== 'not_prioritised' && name !== 'not prioritised';
      }),
    [inquiryPriorityLevels]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedAssignPriorityId('');
    setAssignResponsibleRoleId(null);
    setAssignResponsibleRoleName('');
    setAssignEscalationLevelName('');
    setAssignUsers([]);
    setSelectedAssignUserId('');
    setAssignmentRemarks('');
    let cancelled = false;
    (async () => {
      try {
        const rows = await ticketService.getInquiryPriorityLevelsForCreate();
        if (!cancelled) {
          setInquiryPriorityLevels(Array.isArray(rows) ? rows : []);
        }
      } catch (e) {
        if (!cancelled) {
          setInquiryPriorityLevels([]);
          toast.error(extractErrorMessage(e, 'Failed to load Inquiry Priority Levels'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId]);

  const loadAssignUsersForRole = async (roleId) => {
    if (!roleId) {
      setAssignUsers([]);
      return;
    }
    setLoadingAssignUsers(true);
    try {
      const response = await getUsersWithRole(roleId);
      const payload = response?.data?.data ?? response?.data ?? response;
      const users = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.content) ? payload.content : []);
      setAssignUsers(users);
      if (users.length === 0) {
        toast.error('No users found for the Responsible Role');
      }
    } catch (e) {
      console.error('Failed to load users for responsible role', e);
      toast.error(extractErrorMessage(e, 'Failed to load users for Responsible Role'));
      setAssignUsers([]);
    } finally {
      setLoadingAssignUsers(false);
    }
  };

  const handleAssignPriorityChange = async (priorityLevelId) => {
    setSelectedAssignPriorityId(priorityLevelId ? String(priorityLevelId) : '');
    setSelectedAssignUserId('');
    setAssignUsers([]);
    setAssignResponsibleRoleId(null);
    setAssignResponsibleRoleName('');
    setAssignEscalationLevelName('');
    if (!priorityLevelId || !ticket) return;

    const categoryId = ticket.inquiryCategoryId;
    const subCategoryId = ticket.inquirySubCategoryId;
    if (!categoryId || !subCategoryId) {
      toast.error('Inquiry Category / Sub Category missing on this ticket');
      return;
    }
    try {
      setLoadingAssignUsers(true);
      const role = await ticketService.getLevel1ResponsibleRoleForAssign(
        categoryId,
        subCategoryId,
        Number(priorityLevelId)
      );
      const roleId = role?.responsibleRoleId;
      setAssignResponsibleRoleId(roleId || null);
      setAssignResponsibleRoleName(role?.responsibleRoleName || '—');
      setAssignEscalationLevelName(role?.escalationLevelName || 'Level 1');
      if (!roleId) {
        toast.error('No Level 1 Responsible Role found for this Inquiry Priority. Check Escalation Matrix.');
        setLoadingAssignUsers(false);
        return;
      }
      await loadAssignUsersForRole(roleId);
    } catch (e) {
      console.error('Failed to resolve Level 1 role for priority', e);
      toast.error(extractErrorMessage(e, 'No Level 1 Responsible Role for this Inquiry Priority'));
      setLoadingAssignUsers(false);
    }
  };

  const handleClose = () => {
    if (assigning) return;
    onClose?.();
  };

  const handleAssignInquiry = async () => {
    if (!ticketId) {
      toast.error('Invalid ticket ID');
      return;
    }
    if (!selectedAssignPriorityId) {
      toast.error('Please select an Inquiry Priority Level');
      return;
    }
    if (!selectedAssignUserId) {
      toast.error('Please select a user');
      return;
    }
    if (!assignmentRemarks.trim()) {
      toast.error('Assignment remarks are required');
      return;
    }
    if (assigning) return;

    setAssigning(true);
    try {
      const assignResponse = await ticketService.assignTicket(
        ticketId,
        Number(selectedAssignUserId),
        null,
        Number(selectedAssignPriorityId)
      );
      const responseData = assignResponse?.data;
      if (
        !(responseData?.success || responseData?.status === 200 || isTicketApiSuccess(assignResponse))
      ) {
        toast.error(responseData?.message || 'Failed to assign inquiry');
        return;
      }

      try {
        await ticketService.addReply(ticketId, {
          content: `Assignment remarks: ${assignmentRemarks.trim()}`,
          replyType: 'assignment',
          isInternal: true,
        });
      } catch (noteErr) {
        console.warn('Assignment succeeded but remarks note failed', noteErr);
      }

      toast.success('Inquiry assigned successfully');
      onClose?.();
      onAssigned?.();
    } catch (error) {
      console.error('Error assigning inquiry:', error);
      if (error?.status === 409) {
        toast.error(error.message || 'This inquiry is already assigned to another user.');
      } else {
        toast.error(extractErrorMessage(error, 'Error assigning inquiry. Please try again.'));
      }
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Assign Inquiry" size="md">
      <div className="space-y-4">
        <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-1 text-sm">
          <div>
            <span className="text-gray-500">Ticket:</span>{' '}
            {ticket?.ticketNumber ?? ticketId ?? '—'}
          </div>
          <div>
            <span className="text-gray-500">Inquiry Category:</span>{' '}
            {ticket?.inquiryCategoryName || ticket?.category || '—'}
          </div>
          <div>
            <span className="text-gray-500">Inquiry Sub Category:</span>{' '}
            {ticket?.inquirySubCategoryName || '—'}
          </div>
          <div>
            <span className="text-gray-500">Escalation Level:</span>{' '}
            {assignEscalationLevelName || ticket?.escalationLevelName || 'Level 1'}
          </div>
        </div>

        <QueueConfidentialityLevelControl
          ticketId={ticketId}
          initialLevel={ticket?.confidentialityLevel}
          disabled={assigning}
          onSaved={() => onAssigned?.()}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Inquiry Priority Level <span className="text-red-500 font-semibold">*</span>
          </label>
          <select
            value={selectedAssignPriorityId}
            onChange={(e) => handleAssignPriorityChange(e.target.value)}
            disabled={assigning}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">Select inquiry priority...</option>
            {assignmentInquiryPriorities.map((priority) => {
              const pid = priority.id || priority.inquiryPriorityLevelId;
              return (
                <option key={pid} value={String(pid)}>
                  {priority.priorityLevelName || priority.name}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Responsible Role
          </label>
          <input
            type="text"
            readOnly
            value={assignResponsibleRoleName || (selectedAssignPriorityId ? '—' : 'Select Inquiry Priority first')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            User <span className="text-red-500 font-semibold">*</span>
          </label>
          <select
            value={selectedAssignUserId}
            onChange={(e) => setSelectedAssignUserId(e.target.value)}
            disabled={loadingAssignUsers || !assignResponsibleRoleId || assigning}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">
              {loadingAssignUsers
                ? 'Loading users...'
                : !selectedAssignPriorityId
                  ? 'Select Inquiry Priority first'
                  : 'Select user...'}
            </option>
            {assignUsers.map((user) => {
              const id = user.userId || user.id;
              const name = user.fullName || user.username || user.email || `User ${id}`;
              return (
                <option key={id} value={String(id)}>
                  {name}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Assignment Remarks <span className="text-red-500 font-semibold">*</span>
          </label>
          <textarea
            value={assignmentRemarks}
            onChange={(e) => setAssignmentRemarks(e.target.value)}
            rows={3}
            placeholder="Enter assignment remarks..."
            disabled={assigning}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
        </div>

        <button
          type="button"
          onClick={handleAssignInquiry}
          disabled={
            !selectedAssignPriorityId
            || !selectedAssignUserId
            || !assignmentRemarks.trim()
            || assigning
            || loadingAssignUsers
          }
          className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <UserPlus className="h-5 w-5 shrink-0" />
          {assigning ? 'Assigning…' : 'Assign Inquiry'}
        </button>
      </div>
    </Modal>
  );
}
